// ============================================
// fields/EmailField.js - Email field with validation and highlighting
// ============================================
import React, { useState, useEffect } from 'react';

export const EmailField = ({ field, register, errors, theme, setValue, watch }) => {
  const [value, setValue2] = useState('');
  const [validationStatus, setValidationStatus] = useState('empty'); // empty, partial, valid, invalid, warning
  const [hasUserInput, setHasUserInput] = useState(false);

  // Email validation
  const validateEmail = (email) => {
    if (!email) return 'empty';
    
    // Check if user is still typing the local part
    if (!email.includes('@')) {
      return email.length > 0 ? 'partial' : 'empty';
    }
    
    const [local, domain] = email.split('@');
    
    // Check if still typing domain
    if (!domain || domain.length < 3) return 'partial';
    if (!domain.includes('.')) return 'partial';
    
    // Check for valid domain extension
    const domainParts = domain.split('.');
    const tld = domainParts[domainParts.length - 1];
    if (tld.length < 2) return 'partial';
    
    // Full email regex validation
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    
    if (emailRegex.test(email)) {
      // Check for common typos
      const commonTypos = {
        'gmial.com': 'gmail.com',
        'gmai.com': 'gmail.com',
        'gmil.com': 'gmail.com',
        'gnail.com': 'gmail.com',
        'gmali.com': 'gmail.com',
        'yahooo.com': 'yahoo.com',
        'yaho.com': 'yahoo.com',
        'yahho.com': 'yahoo.com',
        'outlok.com': 'outlook.com',
        'outloo.com': 'outlook.com',
        'hotmial.com': 'hotmail.com',
        'hotmai.com': 'hotmail.com',
        'hotmal.com': 'hotmail.com',
        'iclou.com': 'icloud.com',
        'icloude.com': 'icloud.com',
      };
      
      if (commonTypos[domain]) {
        return 'warning'; // Valid format but likely typo
      }
      
      return 'valid';
    }
    
    // If we have @ and domain but invalid format
    if (local && domain) {
      return 'invalid';
    }
    
    return 'partial';
  };

  // Handle input change
  const handleChange = (e) => {
    let inputValue = e.target.value;
    
    // Mark that user has started typing
    if (!hasUserInput && inputValue !== '') {
      setHasUserInput(true);
    }
    
    // If user clears the field, reset
    if (inputValue === '') {
      setHasUserInput(false);
      setValue2('');
      setValue(field.id, '', { shouldValidate: true, shouldDirty: true });
      return;
    }
    
    // Convert to lowercase for email
    inputValue = inputValue.toLowerCase();
    
    // Basic cleanup - remove spaces
    inputValue = inputValue.replace(/\s/g, '');
    
    setValue2(inputValue);
    setValue(field.id, inputValue, { 
      shouldValidate: true,
      shouldDirty: true 
    });
  };

  // Update validation status
  useEffect(() => {
    setValidationStatus(validateEmail(value));
  }, [value]);

  // Watch for external changes
  useEffect(() => {
    const currentValue = watch(field.id);
    if (currentValue && currentValue !== value) {
      setValue2(currentValue);
      setHasUserInput(true);
    }
  }, [watch(field.id)]);

  // Get status color classes
  const getStatusClasses = () => {
    if (!hasUserInput) return '';
    
    switch (validationStatus) {
      case 'valid':
        return 'bg-green-50 dark:bg-green-950 border-green-500 pr-12';
      case 'warning':
        return 'bg-yellow-50 dark:bg-yellow-950 border-yellow-500 pr-12';
      case 'invalid':
        return 'border-red-400 pr-12';
      case 'partial':
        return value.includes('@') ? 'pr-12' : '';
      default:
        return '';
    }
  };

  // Get icon color for email icon
  const getIconOpacity = () => {
    if (!hasUserInput) return 'opacity-30';
    if (validationStatus === 'valid') return 'opacity-100 text-green-500';
    if (validationStatus === 'warning') return 'opacity-100 text-yellow-500';
    if (validationStatus === 'invalid') return 'opacity-100 text-red-400';
    return 'opacity-60';
  };

  return (
    <div>
      <div className="relative">
        {/* Email icon */}
        <span className={`
          absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-xl
          transition-all duration-200
          ${getIconOpacity()}
        `}>
          📧
        </span>
        
        {/* Email input */}
        <input
          type="email"
          value={value}
          onChange={handleChange}
          placeholder={field.placeholder || 'email@example.com'}
          className={`
            ${theme.input} 
            pl-10
            ${errors[field.id] ? theme.inputError : ''}
            transition-all duration-300
            ${getStatusClasses()}
          `}
          disabled={field.disabled}
          autoComplete="email"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck="false"
        />
        
        {/* Validation indicator */}
        {hasUserInput && value && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
            {validationStatus === 'valid' && (
              <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            )}
            {validationStatus === 'warning' && (
              <svg className="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            )}
            {validationStatus === 'invalid' && value.includes('@') && value.split('@')[1]?.length > 3 && (
              <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
            {validationStatus === 'partial' && value.includes('@') && (
              <div className="text-xs text-gray-400">
                ...
              </div>
            )}
          </div>
        )}
        
        {/* Hidden input for form value */}
        <input
          type="hidden"
          {...register(field.id, field.validation || defaultValidation)}
        />
      </div>
    </div>
  );
};

// Helper function to get typo suggestion
const getCommonTypoSuggestion = (domain) => {
  const commonTypos = {
    'gmial.com': 'gmail.com',
    'gmai.com': 'gmail.com',
    'gmil.com': 'gmail.com',
    'gnail.com': 'gmail.com',
    'gmali.com': 'gmail.com',
    'yahooo.com': 'yahoo.com',
    'yaho.com': 'yahoo.com',
    'yahho.com': 'yahoo.com',
    'outlok.com': 'outlook.com',
    'outloo.com': 'outlook.com',
    'hotmial.com': 'hotmail.com',
    'hotmai.com': 'hotmail.com',
    'hotmal.com': 'hotmail.com',
    'iclou.com': 'icloud.com',
    'icloude.com': 'icloud.com',
  };
  
  return commonTypos[domain] || domain;
};

// Default validation
const defaultValidation = {
  required: 'Email is required',
  pattern: {
    value: /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/,
    message: 'Please enter a valid email address'
  }
};